import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Leaf, Plus } from 'lucide-react';
import { motion } from 'framer-motion';
import { useContent } from '@/context/ContentContext';

const LOOPS = 12;


export const Products: React.FC = () => {
  const { products } = useContent();
  const loopedProducts = Array.from({ length: LOOPS }, () => products).flat();
  const [isVisible, setIsVisible] = useState(false);
  const [cardsVisible, setCardsVisible] = useState(false);
  const [exitProgress, setExitProgress] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasAdvanced, setHasAdvanced] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.02 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);

    const cardsObserver = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setCardsVisible(true); },
      { threshold: 0, rootMargin: '0px 0px -15% 0px' }
    );
    if (scrollerRef.current) cardsObserver.observe(scrollerRef.current);

    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const wh = window.innerHeight;
      if (rect.bottom < wh) {
        const p = 1 - (rect.bottom / wh);
        setExitProgress(Math.max(0, Math.min(p, 1)));
      } else {
        setExitProgress(0);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => { observer.disconnect(); cardsObserver.disconnect(); window.removeEventListener('scroll', handleScroll); };
  }, []);

  // Track which card is in view inside the horizontal scroller (for counter/progress only)
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio > 0.6) {
            const idx = Number((e.target as HTMLElement).dataset.index);
            if (!Number.isNaN(idx)) setActiveIndex(idx);
          }
        });
      },
      { root: scroller, threshold: [0.6, 0.9] }
    );
    cardRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, []);

  // Smooth drag-to-scroll with inertia (mouse)
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let isDown = false;
    let startX = 0;
    let lastX = 0;
    let lastT = 0;
    let velocity = 0;
    let target = 0;
    let current = 0;
    let raf = 0;
    let moved = false;

    const animate = () => {
      // smoother easing toward target + gentler inertia
      current += (target - current) * 0.09;
      if (!isDown) {
        target += velocity;
        velocity *= 0.96;
        if (Math.abs(velocity) < 0.04) velocity = 0;
      }
      el.scrollLeft = current;
      if (isDown || Math.abs(target - current) > 0.3 || velocity !== 0) {
        raf = requestAnimationFrame(animate);
      } else {
        raf = 0;
      }
    };
    const ensureRaf = () => { if (!raf) raf = requestAnimationFrame(animate); };

    const onDown = (e: MouseEvent) => {
      isDown = true;
      moved = false;
      startX = lastX = e.pageX;
      lastT = performance.now();
      velocity = 0;
      current = target = el.scrollLeft;
      el.classList.add('cursor-grabbing');
      ensureRaf();
    };
    const onMove = (e: MouseEvent) => {
      if (!isDown) return;
      const now = performance.now();
      const dx = e.pageX - lastX;
      const dt = Math.max(1, now - lastT);
      // velocity in px/frame (~16ms), low-pass filtered for softness
      const instV = -(dx / dt) * 16;
      velocity = velocity * 0.7 + instV * 0.3;
      lastX = e.pageX;
      lastT = now;
      target -= dx;
      if (Math.abs(e.pageX - startX) > 4) moved = true;
      ensureRaf();
    };
    const onUp = () => {
      if (!isDown) return;
      isDown = false;
      el.classList.remove('cursor-grabbing');
      ensureRaf();
    };
    const onClickCapture = (e: MouseEvent) => {
      if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
    };

    el.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    el.addEventListener('click', onClickCapture, true);
    return () => {
      el.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      el.removeEventListener('click', onClickCapture, true);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const scrollToIndex = (idx: number) => {
    const target = cardRefs.current[idx];
    if (target && scrollerRef.current) {
      target.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  };

  const handleNav = (dir: 1 | -1) => {
    const next = Math.max(0, Math.min(loopedProducts.length - 1, activeIndex + dir));
    if (dir === 1) setHasAdvanced(true);
    scrollToIndex(next);
  };

  const progress = (((activeIndex % products.length) + 1) / products.length) * 100;

  return (
    <section
      id="products"
      ref={sectionRef}
      className="relative w-full py-32 pb-48 bg-[#412018] text-[#412018] overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-[#EAE6D7]"
        style={{
          clipPath: isVisible ? 'inset(0% 0% 0% 0%)' : 'inset(100% 0% 0% 0%)',
          transition: 'clip-path 1.8s cubic-bezier(0.77, 0, 0.175, 1)',
          willChange: 'clip-path',
        }}
      >
        <div className="absolute inset-0 opacity-40 bg-[url('https://www.transparenttextures.com/patterns/cream-paper.png')] pointer-events-none mix-blend-multiply"></div>
      </div>

      <div
        className="relative z-10"
        style={{
          opacity: 1 - exitProgress * 1.2,
          transform: `scale(${1 - exitProgress * 0.05}) translateY(${exitProgress * 50}px)`,
        }}
      >
        {/* Header */}
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto mb-16 relative">
            <div className="text-center max-w-4xl mx-auto">
              <span className={`text-[#67392b] text-sm tracking-[0.3em] uppercase block mb-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                המוצרי בוטיק של רהאם
              </span>
              <h2 className={`font-serif text-[#412018] mb-6 leading-[1.1] tracking-wide transition-all duration-1000 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ fontSize: 'clamp(2.25rem, 4.5vw + 0.5rem, 5.5rem)' }}>
                רוקחות&nbsp;טבעית <span className="text-[#67392b] italic">טיפולית</span>
              </h2>
              <p className={`text-lg text-[#412018]/60 font-light leading-relaxed transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                &nbsp;אוסף מצומצם של פורמולות עתיקות ועוצמתיות לטיפולים משמעותיים לשיפור מהיר ומיידי.
                כל הפורמולות נרקחות במעבדה ובשילוב צמחים ערביים
              </p>
            </div>
          </div>
        </div>

        {/* Horizontal snap scroller */}
        <div className="relative">
          <div
            ref={scrollerRef}
            dir="rtl"
            className="flex gap-6 md:gap-8 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-10 px-6 md:px-[max(1.5rem,calc((100vw-1280px)/2))] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] cursor-grab select-none"
            style={{ scrollPaddingInlineStart: '1.5rem' }}
          >
          {/* Narrow vertical "View all" plate — first (right side in RTL) */}
          <div className="snap-start shrink-0 w-[96px] md:w-[120px] flex">
            <Link
              to="/shop"
              draggable={false}
              className="group flex-1 flex flex-col items-center justify-between py-8 bg-[#3d1f17] text-white transition-colors duration-500 hover:bg-[#67392b]"
            >
              <span className="text-[10px] tracking-[0.3em] uppercase opacity-60 [writing-mode:vertical-rl] rotate-180">
                הבוטיק המלא
              </span>
              <h3 className="font-serif text-4xl md:text-5xl [writing-mode:vertical-rl] rotate-180 leading-none">
                גלו את כל <span className="italic text-[#fbbf25] tracking-wider">האוסף</span>
              </h3>
              <div className="flex flex-col items-center gap-3">
                <span className="text-[10px] font-bold tracking-[0.25em] uppercase [writing-mode:vertical-rl] rotate-180">
                  לכל המוצרים
                </span>
                <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform duration-300" />
              </div>
            </Link>
          </div>

          {loopedProducts.map((product, index) => {
            const animate = index < products.length;
            return (
            <motion.div
              key={`${product.id}-${index}`}
              data-index={index}
              ref={(el) => (cardRefs.current[index] = el)}
              className="snap-start shrink-0 w-[85vw] sm:w-[55vw] md:w-[clamp(340px,32vw,460px)]"
              initial={animate ? { opacity: 0, x: -120, y: -30, rotateY: 18, rotateZ: -2, scale: 0.94, filter: 'blur(8px)' } : false}
              animate={animate && cardsVisible ? { opacity: 1, x: 0, y: 0, rotateY: 0, rotateZ: 0, scale: 1, filter: 'blur(0px)' } : undefined}
              transition={animate ? {
                duration: 1.4,
                delay: index * 0.16,
                ease: [0.22, 1, 0.36, 1],
              } : undefined}
              style={{ transformPerspective: 1800, transformStyle: 'preserve-3d', transformOrigin: 'left center' }}
              whileHover={{ y: -6, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } }}
            >
              <div className={`bg-white border border-[#412018]/5 overflow-hidden relative flex flex-col h-full shadow-sm hover:shadow-2xl hover:shadow-[#67392b]/15 transition-all duration-500 ${activeIndex === index ? 'md:shadow-2xl md:shadow-[#67392b]/15' : 'opacity-80 md:opacity-100'}`}>
                <div className="aspect-square w-full overflow-hidden relative shrink-0 group">
                  <img
                    src={product.image}
                    alt={product.name}
                    draggable={false}
                    className="w-full h-full object-cover transition-transform duration-[2000ms] ease-luxury group-hover:scale-105 grayscale-0"
                  />
                  <div className="absolute top-4 right-4 z-20 bg-white/90 backdrop-blur px-3 py-1 rounded-sm flex items-center gap-2 shadow-sm">
                    <Leaf size={12} className="text-green-700" />
                    <span className="text-[10px] uppercase tracking-wider text-[#412018]">100% טבעי</span>
                  </div>
                </div>

                <div className="px-4 md:px-6 pt-4 md:pt-6 pb-2 flex flex-col flex-1 relative z-20 text-right" dir="rtl">
                  <p className="text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-[#67392b] font-bold mb-2">
                    {product.sub}
                  </p>
                  <div className="flex justify-between items-baseline mb-3 gap-3">
                    <h3 className="font-serif leading-[0.95] tracking-wide text-[#412018]" style={{ fontSize: 'clamp(1.6rem, 2.4vw + 0.4rem, 2.6rem)' }}>
                      {product.name}
                    </h3>
                    <span className="text-base font-sans text-[#412018] whitespace-nowrap opacity-80 pt-1">
                      {product.price}
                    </span>
                  </div>
                  <p className="text-[12px] md:text-[13px] text-[#412018]/70 font-light leading-[1.6] mb-5 flex-grow line-clamp-4">
                    {product.description}
                  </p>
                </div>

                <div className="mt-auto flex w-full border-t border-[#412018]/10">
                  <Link to={`/shop/${product.id}`} className="flex-[1] bg-[#F9F7F2] hover:bg-[#EAE6D7] text-[#412018] py-4 px-4 flex items-center justify-center border-l border-[#412018]/10 transition-colors duration-300 group/sec-btn">
                    <div className="flex items-center gap-2">
                      <Plus size={12} className="text-[#412018]/40 group-hover/sec-btn:rotate-90 transition-transform duration-300" />
                      <span className="text-[10px] font-bold tracking-[0.15em] uppercase">פרטים</span>
                    </div>
                  </Link>
                  <Link to={`/shop/${product.id}`} className="flex-[2.5] bg-[#3d1f17] hover:bg-[#67392b] text-[#fbbf25] py-4 px-6 flex items-center justify-center gap-3 transition-all duration-500 group/main-btn relative overflow-hidden">
                    <span className="relative z-10 text-[11px] font-bold tracking-[0.2em] uppercase whitespace-nowrap">הוספה לסל</span>
                    <ArrowLeft size={16} className="relative z-10 text-[#fbbf25] group-hover/main-btn:-translate-x-1 transition-transform duration-300" />
                    <div className="absolute inset-0 bg-white/5 translate-x-full group-hover/main-btn:translate-x-[-150%] transition-transform duration-700 skew-x-12 pointer-events-none"></div>
                  </Link>
                </div>
              </div>
            </motion.div>
            );
          })}
          </div>

          {/* Side arrows over cards */}
          <button
            aria-label="Previous"
            onClick={() => handleNav(-1)}
            className={`hidden md:flex absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/40 backdrop-blur border border-[#412018]/10 items-center justify-center text-[#412018] hover:bg-[#3d1f17]/80 hover:text-[#fbbf25] hover:border-[#3d1f17] transition-all duration-500 shadow-lg shadow-[#412018]/10 ${hasAdvanced ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          >
            <ArrowRight size={18} />
          </button>
          <button
            aria-label="Next"
            onClick={() => handleNav(1)}
            className="hidden md:flex absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/40 backdrop-blur border border-[#412018]/10 items-center justify-center text-[#412018] hover:bg-[#3d1f17]/80 hover:text-[#fbbf25] hover:border-[#3d1f17] transition-all duration-300 shadow-lg shadow-[#412018]/10"
          >
            <ArrowLeft size={18} />
          </button>
        </div>

        {/* Progress bar */}
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="h-[2px] bg-[#412018]/10 relative overflow-hidden" dir="rtl">
              <div
                className="absolute top-0 right-0 h-full bg-[#67392b] transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="mt-4 text-center text-[#412018]/50 text-xs tracking-[0.2em] uppercase">
              גררו לצדדים לגילוי האוסף ←
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};